import { productSummary } from "./ProductDescription";
import React from "react";
import { Link, useSearchParams } from "react-router-dom";

import { bringIntoView } from "../navigation/useScrollIntoViewOnChange";
import useFetch from "../Network/useFetch";

// "Request a quote" skips the builder and opens the quote form with the
// machine filled in; "Customise this machine" is the route to the builder.
export function machineQuoteParams(item: { _id: string; name?: string }) {
  const name = (item.name || "Gaming machine").trim();
  return new URLSearchParams({
    subject: `Gaming machine quote: ${name}`.slice(0, 150),
    message: `Machine: ${name}\nMachine link: https://technogeniousja.com/game-product-detail/${item._id}\nHardware: please recommend.\nNumber of machines: \nLocation: `,
  }).toString();
}

export default function GamingBox() {
  // The page number lives in the address (?page=2), so Back from a machine
  // returns to the same page of the list.
  const [params, setParams] = useSearchParams();
  const raw = Number(params.get("page") || 1);
  const page = Number.isSafeInteger(raw) && raw > 0 && raw <= 1000 ? raw : 1;
  const pageSize = 6;
  // Next/Previous land at the top of the machine list, not at its old bottom.
  const changePage = (next: number) => {
    bringIntoView(document.getElementById("machines"));
    const updated = new URLSearchParams(params);
    if (next > 1) updated.set("page", String(next)); else updated.delete("page");
    setParams(updated, { preventScrollReset: true });
  };
  // The machine page's "All gaming machines" link returns here.
  const listReturn = { machinesReturn: `/gaming-machines${page > 1 ? `?page=${page}` : ""}#machines` };
  // Keep the current machines on screen until the next page arrives.
  const { data, isLoading, isError, refetch } = useFetch(
    `${process.env.REACT_APP_SERVER_URL}/product/gaming-machine?page=${page}&pageSize=${pageSize}`,
    ["fetch-gaming-machines", page, pageSize],
    { keepPreviousData: true }
  );
  if (isLoading)
    return (
      <p className="eq-state" role="status">
        Loading gaming machines…
      </p>
    );
  if (isError)
    return (
      <div className="eq-state" role="alert">
        <h3>We couldn’t load the gaming machines.</h3>
        <p>
          Try again, or contact our team for details of available machines.
        </p>
        <button className="eq-primary" onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  const items = data?.data?.results || [];
  const pages = Math.max(
    1,
    Math.ceil((data?.data?.totalRecords || 0) / pageSize)
  );
  if (!items.length)
    return (
      <div className="eq-state">
        <h3>Let’s find the right gaming machine for you.</h3>
        <p>
          No machine listings are available in this catalogue yet. Our team can
          help with available models, specifications, and quotations.
        </p>
        <Link className="eq-primary" to="/contact">
          Ask about available gaming machines →
        </Link>
        {page > 1 && (
          <button onClick={() => changePage(1)}>Return to first page</button>
        )}
      </div>
    );
  return (
    <>
      <p className="eq-count">
        {data.data.totalRecords}{" "}
        {data.data.totalRecords === 1 ? "listing" : "listings"} · Contact us
        for pricing and availability
      </p>
      <div className="eq-grid">
        {items.map((item) => (
          <article key={item._id}>
            <Link
              className="eq-image"
              to={`/game-product-detail/${encodeURIComponent(item._id)}`}
                state={listReturn}
            >
              <img
                src={item.mainImage || "/catalog/placeholder.svg"}
                alt={item.name}
                loading="lazy"
                onError={(e) => {
                  if (!e.currentTarget.src.endsWith("/catalog/placeholder.svg"))
                    e.currentTarget.src = "/catalog/placeholder.svg";
                }}
              />
            </Link>
            <div className="eq-card-body">
              <h3>
                <Link
                  to={`/game-product-detail/${encodeURIComponent(item._id)}`}
                state={listReturn}
                >
                  {item.name}
                </Link>
              </h3>
              <p>
                {productSummary(item.description) ||
                  "Contact our team for specifications and availability."}
              </p>
              {Array.isArray(item.features) && (
                <ul className="gm-card-features">
                  {item.features.slice(0, 3).map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              )}
              <div className="gm-card-actions">
                <Link
                  className="gm-primary"
                  to={`/build-your-machine?machine=${encodeURIComponent(
                    item._id
                  )}`}
                >
                  Customise this machine →
                </Link>
                <Link to={`/request-quote?${machineQuoteParams(item)}`}>
                  Request a quote
                </Link>
              </div>
              <Link
                className="eq-detail"
                to={`/game-product-detail/${encodeURIComponent(item._id)}`}
                state={listReturn}
              >
                View gaming machine →
              </Link>
            </div>
          </article>
        ))}
      </div>
      {pages > 1 && (
        <nav className="eq-pagination" aria-label="Gaming machine pages">
          <button disabled={page === 1} onClick={() => changePage(page - 1)}>
            Previous
          </button>
          <span>
            Page {page} of {pages}
          </span>
          <button disabled={page >= pages} onClick={() => changePage(page + 1)}>
            Next
          </button>
        </nav>
      )}
    </>
  );
}
